import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getTenantSlug } from "@/lib/tenant";

/* Cache is keyed by tenant slug ("" for the root/Allure studio) so visiting
   yourstudio.lumetiercreatives.com after allure-image.com doesn't reuse
   Allure's hero/about/services. The axios interceptor handles the actual
   `?studio=<slug>` query param — we just need to make sure the cache
   doesn't pollute across tenants. */
const caches = new Map();   // slug -> settings doc
const subs = new Map();      // slug -> Set<callback>
const inflight = new Map();  // slug -> Promise (de-dupe parallel fetches)

/* localStorage cache — survives across page loads so returning visitors get
   an INSTANT correct first paint (no API roundtrip = no flash of fallback
   content). The stored value is the same shape as the in-memory cache.
   Keyed by slug so multi-tenant cohabitation in the same browser is safe.

   Why not also use it for cache invalidation? Because settings can change
   server-side (the photographer edits their hero) and we don't want a
   visitor stuck on a 2-week-old cached copy. So: hydrate from localStorage
   SYNCHRONOUSLY on mount (eliminates flash for return visitors), then ALWAYS
   refetch in the background and update both caches with whatever the API
   returns. The refetch usually completes in <300ms; the new data either
   matches what was already on screen (zero visible change) or replaces a
   stale field (graceful update).

   PREFIX VERSION: bump this whenever a backend/frontend change could
   leave already-cached docs pointing at the WRONG studio. v1 → v2
   shipped on 2026-06-29 alongside the synchronous `_currentSlug`
   assignment in TenantProvider, which fixed a race where pre-fix
   visitors had their tenant cache filled with Allure-Image data
   (the platform's default-tenant fallback). Bumping the prefix
   forces every browser to drop the polluted v1 cache and re-fetch
   the correct per-tenant doc on the next page load.

   When to bump in future: a) shape of the doc changes incompatibly,
   b) a multi-tenant leak fix lands, c) field-level encryption keys
   rotate. NEVER reuse a previous version number. */
const LS_PREFIX = "lumetier:site_settings:v2:";

function _lsKey(slug) {
  return `${LS_PREFIX}${slug || "_root"}`;
}

function _readLs(slug) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(_lsKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Sanity guard against corrupted storage — must look like a site_settings doc.
    if (parsed && typeof parsed === "object" && (parsed.studio_id !== undefined || parsed.studio_name !== undefined)) {
      return parsed;
    }
    return null;
  } catch (_) {
    return null;
  }
}

function _writeLs(slug, data) {
  if (typeof window === "undefined" || !data) return;
  try {
    window.localStorage.setItem(_lsKey(slug), JSON.stringify(data));
  } catch (_) {
    // QuotaExceeded / private mode — silently skip; in-memory cache still works.
  }
}

function _clearLs(slug) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(_lsKey(slug));
  } catch (_) { /* noop */ }
}

function getSubs(slug) {
  if (!subs.has(slug)) subs.set(slug, new Set());
  return subs.get(slug);
}

export function useSiteSettings() {
  const slug = getTenantSlug() || "";
  // SYNCHRONOUS hydrate priority:
  //   1. In-memory cache (already populated by a prior mount in this session)
  //   2. localStorage cache (populated on prior pageview / prior session)
  //   3. null — first-time visitor with no cache, will see skeleton until fetch
  const memCached = caches.get(slug);
  const lsCached = memCached ? null : _readLs(slug);
  const initial = memCached || lsCached || null;
  const [settings, setSettings] = useState(initial);
  // `loading=true` means "no usable data yet (neither memory nor localStorage)
  // — show a skeleton". Once we have ANYTHING worth painting, loading goes
  // false and the page renders, even if the background refetch is still flying.
  const [loading, setLoading] = useState(!initial);

  // Seed the in-memory cache from localStorage on the very first mount so
  // subsequent useSiteSettings() consumers in this session skip the LS read.
  if (lsCached && !memCached) {
    caches.set(slug, lsCached);
  }

  useEffect(() => {
    let alive = true;
    const cached = caches.get(slug);
    if (cached) {
      setSettings(cached);
      setLoading(false);
    }
    const sub = (next) => { if (alive) setSettings(next); };
    getSubs(slug).add(sub);

    // ALWAYS refetch in the background, even if we have a cached copy — the
    // photographer may have edited their hero since the last visit. The
    // refetch updates both caches and fans out to every mounted consumer.
    if (!inflight.has(slug)) {
      inflight.set(slug, api.get("/site").then((r) => {
        caches.set(slug, r.data);
        _writeLs(slug, r.data);
        getSubs(slug).forEach((s) => s(r.data));
        inflight.delete(slug);
        return r.data;
      }).catch((err) => {
        inflight.delete(slug);
        throw err;
      }));
    }
    inflight.get(slug).finally(() => { if (alive) setLoading(false); });

    return () => {
      alive = false;
      getSubs(slug).delete(sub);
    };
  }, [slug]);

  return { settings, loading };
}

export function updateCachedSettings(next) {
  const slug = getTenantSlug() || "";
  caches.set(slug, next);
  _writeLs(slug, next);
  getSubs(slug).forEach((s) => s(next));
  // Cross-slug invalidation. The admin edits a studio while logged in under
  // its admin slug ("" for the platform-owner Allure session, or the tenant
  // slug otherwise) — but the same studio may be cached under additional
  // slug keys from earlier mounts (e.g. previewing `/s/allure-image` in
  // another tab in the same SPA session). Clearing every OTHER entry forces
  // a re-fetch on those routes so the photographer doesn't see stale copy.
  for (const otherSlug of Array.from(caches.keys())) {
    if (otherSlug !== slug) {
      caches.delete(otherSlug);
      _clearLs(otherSlug);
      getSubs(otherSlug).forEach((s) => s(null));
    }
  }
}

/* Same-studio invalidator the admin can call after PATCH /api/admin/site to
   force every mounted useSiteSettings consumer to drop its cache and re-fetch.
   Use this when the new doc isn't available client-side (e.g. cross-tab
   broadcast via BroadcastChannel) but you still want a refresh. */
export function invalidateAllSiteSettings() {
  for (const slug of Array.from(caches.keys())) {
    caches.delete(slug);
    _clearLs(slug);
  }
  // Tell every subscriber to drop its current value; the useEffect re-run
  // will refetch on next mount or next slug change.
  for (const slug of Array.from(subs.keys())) {
    getSubs(slug).forEach((s) => s(null));
  }
}
